<template>
    <div class="flex flex-col">
        <div
            class="wizard-header-carousel border-b border-slate-200 dark:border-slate-700 md:pt-[34px] relative"
            ref="header-container"
        >
            <CardCarousel
                v-if="searchType === 'Trip wizard'"
                :numCards="localAirports.length"
                ref="carousel"
                :cardWidth="245"
                :initialSlide="startingIndex"
                :snapAlign="snapAlign"
                :cards="localAirports"
            >
                <template #carouselCard="{ card, maxWidth }">
                    <div
                    @click="changeTripLeg(card)"
                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight flex flex-col justify-start items-center pb-3.5 px-1.5 carousel__item truncate max-w-full w-[245px] cursor-pointer"
                        :class="{
                            'border-b border-orange':
                                activeTripLeg == card.ordinal,
                        }"
                        :title="`${card.name}
${card.airline.name} · ${card.class.name}`"
                    >
                        <span class="truncate max-w-full">
                            {{ card.name }}
                        </span>
                        <span
                            class="text-xs text-slate-400 truncate max-w-full"
                        >
                            {{ card.airline.name }} · {{ card.class.name }}
                        </span>
                    </div>
                </template>
            </CardCarousel>
            <div v-else class="flex justify-between items-center gap-2">
                <div
                    v-if="activeAirport"
                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight whitespace-nowrap w-fit flex flex-col justify-start items-start py-3.5"
                >
                    {{ activeAirport.name }}
                </div>
                <FavButton
                    buttonClass="md:hidden w-5 !justify-end"
                    :isFavorite="isFavoriteAirport || false"
                    @toggleFavorite="handleToggleFavorite"
                    id="hs-lounge-favorite-airport-mobile-btn"
                />
            </div>
        </div>
        <div
            class="self-stretch md:pt-8 pt-2 justify-between items-start flex md:flex-row flex-col-reverse gap-3 md:gap-0"
        >
            <div
                class="text-slate-600 dark:text-slate-300 dark:text-slate-400 text-sm font-medium leading-tight"
                :style="{
                    visibility: searchResultsCount ? 'visible' : 'hidden',
                }"
            >
                {{ searchResultsCount }} lounges
            </div>
            <div class="justify-center items-center gap-6 flex">
                <div
                    class="rounded justify-start items-center gap-2 hidden md:flex"
                >
                    <FavButton
                        :isFavorite="isFavoriteAirport || false"
                        @toggleFavorite="handleToggleFavorite"
                        id="hs-lounge-favorite-airport-btn"
                    />

                    <button
                        class="btn-link text-slate-800 dark:text-slate-100 dark:text-slate-200 text-sm font-medium leading-tight whitespace-nowrap"
                        @click.prevent="handleToggleFavorite"
                        id="hs-lounge-toggle-favorite-text-btn"
                    >
                        {{
                            isFavoriteAirport ? 'Remove from ' : 'Save to '
                        }}favorites
                    </button>
                </div>
                <button
                    @click.prevent="openMap"
                    class="btn-base btn-hollow rounded justify-start items-center gap-2 md:flex text-slate-800 dark:text-slate-100 dark:text-slate-200 text-sm font-medium leading-tight disabled:opacity-50 disabled:bg-transparent disabled:text-slate-600 dark:text-slate-300 dark:disabled:text-slate-500 dark:text-slate-400 hidden"
                    :disabled="!searchResultsCount"
                    id="hs-lounge-open-map-view-btn"
                >
                    <Icon icon="map" />
                    Open map view
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import {
    ref,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    useTemplateRef,
    computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useLoungesStore } from '@/store/lounges';
import CardCarousel from '@/components/ui/card-carousel/card-carousel.vue';
import FavButton from '@/components/ui/fav-button/fav-button.vue';
import Icon from '@/components/ui/icon/icon.vue';

const loungesStore = useLoungesStore();
const {
    activeAirport,
    activeTripLeg,
    searchResultsCount,
    searchType,
    userFlights,
} = storeToRefs(loungesStore);
const {
    resetFilters,
    searchWizard,
    setActiveTripLeg,
    setMapType,
    setShowMap,
    toggleFavorite,
} = loungesStore;

const isFavoriteAirport = ref(activeAirport.value?.favorite);
const localAirports = ref([]);
const headerContainer = useTemplateRef('header-container');
const containerWidth = ref(0);
const snapAlign = ref('center');

const changeTripLeg = (flight) => {
    setActiveTripLeg(flight.ordinal);
    resetFilters();
    searchWizard();
};

const handleToggleFavorite = async () => {
    if (!activeAirport.value?.id) {
        console.error('No valid airport to favorite:', activeAirport.value);
        return;
    }
    isFavoriteAirport.value = !isFavoriteAirport.value;

    const airport = activeAirport.value;

    const res = await toggleFavorite(airport, 'airport');
    if (!res || res.status !== 200) {
        isFavoriteAirport.value = !isFavoriteAirport.value;
    }
};

function getIndex(x, y) {
    return (x - y + localAirports.value.length) % localAirports.value.length;
}

const updateCarouselSettings = () => {
    const width = containerWidth.value;
    let offset;

    if (width === 0) {
        offset = 0;
        return;
    }

    if (width < 525) {
        offset = 1;
        snapAlign.value = 'center';
    } else if (width < 768) {
        offset = 1;
        snapAlign.value = 'center';
    } else if (width < 1024) {
        offset = 2;
        snapAlign.value = 'center';
    } else if (width < 1280) {
        offset = 2;
        snapAlign.value = 'start';
    } else {
        offset = 4;
        snapAlign.value = 'start';
    }

    startingOffset.value = offset;
};

const startingOffset = ref(0);

const startingIndex = computed(() => {
    return getIndex(activeTripLeg.value, startingOffset.value);
});

const getUniqueTripData = () => {
    if (!userFlights.value.length) return { airports: [], flights: [] };

    // Create a unique key for each airport + airline + class combination
    const getAirportKey = (airport, flight) => {
        return `${airport.code}_${flight.airline.code}_${flight.class.value}`;
    };

    const uniqueFlights = userFlights.value.reduce(
        (acc, flight, i) => {
            const to = {
                ...flight.to,
                class: flight.class,
                airline: flight.airline,
                ordinal: i * 2 + 2,
                id: `${flight.to.code}_${flight.airline.code}_${flight.class.value}`
            };

            const from = {
                ...flight.from,
                class: flight.class,
                airline: flight.airline,
                ordinal: i * 2 + 1,
                id: `${flight.from.code}_${flight.airline.code}_${flight.class.value}` 
            };

            const fromKey = getAirportKey(flight.from, flight);
            const toKey = getAirportKey(flight.to, flight);


            const fromExists = acc.airportKeys.has(fromKey);
            const toExists = acc.airportKeys.has(toKey);

            if (!fromExists || !toExists) {
                acc.flights.push(flight);
            }

            if (!fromExists) {
                acc.airports.push(from);
                acc.airportKeys.add(fromKey);
            }

            if (!toExists) {
                acc.airports.push(to);
                acc.airportKeys.add(toKey);
            }

            return acc;
        },
        { airports: [], flights: [], airportKeys: new Set() }
    );

    uniqueFlights.airports.sort((a, b) => (a.ordinal > b.ordinal ? 1 : -1));

    delete uniqueFlights.airportKeys;

    return uniqueFlights;
};

const getContainerWidth = () => {
    if (headerContainer.value) {
        containerWidth.value = headerContainer.value.offsetWidth;
    }
};

watch(
    () => activeAirport.value,
    () => {
        isFavoriteAirport.value = activeAirport.value?.favorite;
    }
);

watch(
    () => containerWidth.value,
    () => {
        updateCarouselSettings();
    }
);

onMounted(() => {
    const { airports } = getUniqueTripData();
    localAirports.value = airports;

    nextTick(() => {
        getContainerWidth();
        updateCarouselSettings();
        window.addEventListener('resize', getContainerWidth);
    });
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', getContainerWidth);
});

const openMap = () => {
    setMapType('searchResults');
    setShowMap(true);
};
</script>

<style lang="scss" scoped>
.mask {
    z-index: 10;
    background: linear-gradient(to left, transparent, white 100%);
}
</style>
